import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import type { SurveyCrosstabDto, SurveyCrosstabSummaryDto } from '@/shared/api/types'
import { crosstab, getInstrument, listCrosstabs, listPolls, listVersions } from './api'
import { PollSelect } from './PollSelect'
import { Pill, count, num, pct } from './vocab'

/**
 * A p-value, or an honest dash.
 *
 * `Number(null)` is 0 and 0 is below every threshold, so a `?? 0` here would
 * print "p < 0.0001" for a table that has no p-value at all — the strongest
 * claim in the feature, manufactured out of a null.
 */
const pLabel = (v: string | number | null) =>
  v === null || v === undefined ? '—' : Number(v) < 0.0001 ? '< 0.0001' : num(v, 4)

/**
 * Two questions, and whether the association means anything.
 *
 * ## The gate is the feature
 *
 * Cochran's rule: at least 80% of cells with an expected count of 5 or more, and
 * no cell below 1. When it fails there is NO p-value and NO effect size — the
 * table is shown with a sentence saying why. PollSphere has no such check and
 * will print `p = 0.0067, Cramér's V = 0.62 (large)` on a 6×7 table with twenty
 * respondents, under the prose "The association is strong enough to be
 * practically meaningful".
 *
 * A multi-response question is tabulated but not tested: one respondent choosing
 * two options appears in two cells, and chi-square assumes each observation falls
 * in exactly one.
 *
 * ## The list shape is not the detail shape
 *
 * `GET /surveys/{id}/crosstabs` returns `CrosstabSummary` — no `cells`, no
 * `surveyId`, no question TEXT. Only the POST returns a table. So the history
 * panel below lists what has been computed and clicking a row RECOMPUTES it
 * through `crosstab(surveyId, rowQuestionId, colQuestionId)`; it cannot render
 * one, because the cells were never sent. The poll id for that call comes from
 * the URL rather than from the row, since the summary does not carry one.
 */
export function CrosstabsPage() {
  const canAnalyse = useCan('survey:analyse')
  const [params, setParams] = useSearchParams()
  const pollId = params.get('poll')
  const [rowQuestion, setRowQuestion] = useState('')
  const [colQuestion, setColQuestion] = useState('')
  const [table, setTable] = useState<SurveyCrosstabDto | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const polls = useLoad(() => listPolls({}, 0, 100), [])
  const versions = useLoad(
    () => (pollId ? listVersions(pollId) : Promise.resolve([])), [pollId])
  const versionId = (versions.data ?? []).slice()
    .sort((a, b) => b.versionNo - a.versionNo)[0]?.id ?? null
  const instrument = useLoad(
    () => (versionId ? getInstrument(versionId) : Promise.resolve(null)), [versionId])
  const history = useLoad(
    () => (pollId ? listCrosstabs(pollId) : Promise.resolve([])), [pollId])

  if (!canAnalyse) return <Forbidden what="crosstabs" />

  const setPoll = (id: string) => {
    const next = new URLSearchParams(params)
    if (id) next.set('poll', id)
    else next.delete('poll')
    setParams(next, { replace: true })
    setTable(null)
    setRowQuestion('')
    setColQuestion('')
    setError(null)
  }

  /**
   * The ids are arguments rather than read from state, because replaying a
   * previously computed table sets the two pickers and runs in the same click —
   * and a `setState` in that click has not landed by the time this reads it.
   */
  async function runWith(rowId: string, colId: string) {
    if (!pollId || !rowId || !colId) return
    setBusy(true)
    setError(null)
    try {
      setTable(await crosstab(pollId, rowId, colId))
      // The POST persists the table, so a first computation is a new history row.
      history.reload()
    } catch (e) {
      setError(explain(e))
      setTable(null)
    } finally {
      setBusy(false)
    }
  }

  const run = () => runWith(rowQuestion, colQuestion)

  function replay(summary: SurveyCrosstabSummaryDto) {
    setRowQuestion(summary.rowQuestionId)
    setColQuestion(summary.colQuestionId)
    void runWith(summary.rowQuestionId, summary.colQuestionId)
  }

  const questions = (instrument.data?.questions ?? []).filter((q) => q.options.length > 0)
  const rowLabels = [...new Set((table?.cells ?? []).map((c) => c.rowLabel ?? '—'))]
  const colLabels = [...new Set((table?.cells ?? []).map((c) => c.colLabel ?? '—'))]
  const cellAt = (row: string, col: string) =>
    (table?.cells ?? []).find((c) => (c.rowLabel ?? '—') === row && (c.colLabel ?? '—') === col)

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Crosstabs"
        description="Counts, expected counts, standardised residuals — and a test only where the assumptions hold."
        right={<PollSelect polls={polls.data?.content ?? []} value={pollId} onChange={setPoll} />}
      />

      <ErrorStrip message={error ?? instrument.error ?? history.error} />

      {!pollId ? (
        <Note tone="info">Pick a poll.</Note>
      ) : instrument.loading ? <LoadingBlock label="Loading the instrument…" /> : (
        <>
          <Panel title="Cross two questions">
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1.5 text-erp-xs">
                Rows
                <select className="erp-input h-9 min-w-[220px]" value={rowQuestion}
                  onChange={(e) => setRowQuestion(e.target.value)}>
                  <option value="">Choose…</option>
                  {questions.map((q) => (
                    <option key={q.id} value={q.id}>{q.code} · {q.text}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1.5 text-erp-xs">
                Columns
                <select className="erp-input h-9 min-w-[220px]" value={colQuestion}
                  onChange={(e) => setColQuestion(e.target.value)}>
                  <option value="">Choose…</option>
                  {questions.filter((q) => q.id !== rowQuestion).map((q) => (
                    <option key={q.id} value={q.id}>{q.code} · {q.text}</option>
                  ))}
                </select>
              </label>
              <button type="button" className="erp-btn-primary h-9 text-erp-sm"
                disabled={busy || !rowQuestion || !colQuestion} onClick={run}>
                {busy ? 'Computing…' : 'Cross'}
              </button>
            </div>
            {questions.length < 2 && (
              <div className="mt-3">
                <Note tone="info">
                  This instrument has fewer than two questions with options. A crosstab needs
                  two categorical questions.
                </Note>
              </div>
            )}
          </Panel>

          <Panel
            title="Previously computed"
            description="Every table computed for this poll, newest first. Clicking one recomputes it."
          >
            {history.loading && (history.data ?? []).length === 0
              ? <LoadingBlock label="Loading what has been computed…" /> : (
                <ErpTable head={(
                  <>
                    <Th>Rows</Th>
                    <Th>Columns</Th>
                    <Th className="text-right">Observations</Th>
                    <Th>Test</Th>
                    <Th>Computed</Th>
                  </>
                )}>
                  {(history.data ?? []).length === 0 ? (
                    <EmptyRow colSpan={5} message={
                      'Nothing has been crossed for this poll yet. Pick two questions above.'
                    } />
                  ) : (history.data ?? [])
                    .slice()
                    .sort((a, b) => b.computedAt.localeCompare(a.computedAt))
                    .map((row) => (
                      <tr key={row.id} className="hover:bg-muted/40 cursor-pointer"
                        onClick={() => replay(row)}>
                        {/*
                          Question CODES, not text. `CrosstabSummary` has no
                          `rowQuestionText`/`colQuestionText` — only the POST
                          response carries them — and looking the text up from the
                          instrument would attach the CURRENT wording to a table
                          computed against an earlier published version.
                        */}
                        <Td className="font-mono text-erp-xs text-chocolate">
                          {row.rowQuestionCode}
                        </Td>
                        <Td className="font-mono text-erp-xs text-chocolate">
                          {row.colQuestionCode}
                        </Td>
                        <Td className="text-right tabular-nums">{count(row.grandTotal)}</Td>
                        <Td>
                          {row.assumptionsMet ? (
                            <Pill>{`p ${pLabel(row.pValue)} · V ${num(row.cramersV)}`}</Pill>
                          ) : (
                            <Pill tone="bg-amber-500/10 text-amber-600 border-amber-500/30">
                              assumptions unmet — no test
                            </Pill>
                          )}
                        </Td>
                        <Td className="text-muted-foreground">{fmtDateTime(row.computedAt)}</Td>
                      </tr>
                    ))}
                </ErpTable>
              )}
            <div className="mt-3">
              <Note tone="info">
                These rows are summaries: the server keeps the statistics but not the cells, so
                a row cannot be opened — it is recomputed against the interviews collected as of
                now. A table computed last week and recomputed today will disagree if fieldwork
                continued, and that disagreement is real rather than a bug.
              </Note>
            </div>
          </Panel>

          {table && (
            <>
              <Panel
                title={`${table.rowQuestionCode} × ${table.colQuestionCode}`}
                description={`${count(table.grandTotal)} observations`}
              >
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <Pill>{`chi-square ${num(table.chiSquare)}`}</Pill>
                  <Pill>{`df ${table.df ?? '—'}`}</Pill>
                  {table.assumptionsMet ? (
                    <>
                      {/*
                        * `?? 0` here would print "p < 0.0001" for a p-value the
                        * server declined to compute — the single most confident
                        * sentence in statistics, printed on the absence of one.
                        * A null p is an em dash.
                        */}
                      <Pill>{`p ${table.pValue == null ? '—'
                        : Number(table.pValue) < 0.0001 ? '< 0.0001'
                        : num(table.pValue, 4)}`}</Pill>
                      <Pill>{`Cramér's V ${num(table.cramersV)}`}</Pill>
                    </>
                  ) : (
                    <Pill tone="bg-amber-500/10 text-amber-600 border-amber-500/30">
                      no test
                    </Pill>
                  )}
                </div>

                {!table.assumptionsMet && table.assumptionNote && (
                  <div className="mb-3">
                    {/*
                      The sentence, in place of the statistic. Not a tooltip and
                      not a footnote: the reason the test is absent is the most
                      important thing on this panel.
                    */}
                    <Note tone="info">{table.assumptionNote}</Note>
                  </div>
                )}

                <div className="axis-table-scroll">
                  <ErpTable head={(
                    <>
                      <Th>{table.rowQuestionCode}</Th>
                      {colLabels.map((col) => <Th key={col} className="text-right">{col}</Th>)}
                    </>
                  )}>
                    {rowLabels.map((row) => (
                      <tr key={row} className="hover:bg-muted/40">
                        <Td>{row}</Td>
                        {colLabels.map((col) => {
                          const cell = cellAt(row, col)
                          const residual = Number(cell?.stdResidual ?? 0)
                          return (
                            <Td key={col} className="text-right tabular-nums">
                              <span
                                // The RESIDUAL is what says which cells drive the
                                // association. Above |2| it is worth an eye, so it
                                // gets the one accent on this screen.
                                className={Math.abs(residual) >= 2 ? 'text-chocolate font-medium' : ''}
                                title={cell
                                  ? `expected ${num(cell.expected)} · residual ${num(cell.stdResidual)}`
                                  : undefined}
                              >
                                {count(cell?.observed ?? 0)}
                              </span>
                              <span className="block text-erp-xs text-muted-foreground">
                                {pct(cell?.rowPct)}
                              </span>
                            </Td>
                          )
                        })}
                      </tr>
                    ))}
                  </ErpTable>
                </div>
              </Panel>

              <Note tone="info">
                Percentages are of the ROW. Expected counts and standardised residuals are in
                each cell's tooltip — a residual beyond ±2 is where the association actually
                lives, and a chi-square on its own only says that one exists.
              </Note>
            </>
          )}
        </>
      )}
    </div>
  )
}
